import { cva } from 'class-variance-authority';

export const switcherContainer = cva(
  [
    'offset-switcher-content-v2',
    'mt-0',
    'flex',
    'w-full',
    'flex-col',
    'justify-between',
    'gap-0',
    'lg:gap-8',
    'md:mt-4',
    'lg:flex-row',
    'rounded-[4px]',
    'lg:rounded-none',
  ],
  {
    variants: {
      isMobile: {
        light: ['border', 'border-primary-600'],
        dark: [],
      },
    },
  },
);

export const switcherTabScrollContainer = cva(
  ['bg-transparent', 'border-none', 'max-h-[694px]', 'w-[30%]', 'xl:min-w-[324px]', 'relative'],
  {
    variants: {
      isAtTop: {
        false: [
          'before:content-[""]',
          'before:block',
          'before:bg-gradient-to-t',
          'before:from-transparent',
          'before:absolute',
          'before:-top-px',
          'before:h-[58px]',
          'before:w-full',
          'before:z-10',
          'before:pointer-events-none',
        ],
      },
      isAtBottom: {
        false: [
          'after:content-[""]',
          'after:block',
          'after:bg-gradient-to-b',
          'after:from-transparent',
          'after:absolute',
          'after:-bottom-px',
          'after:h-[58px]',
          'after:w-full',
          'after:z-10',
          'after:pointer-events-none',
        ],
      },
      background: {
        white: ['before:to-white', 'after:to-white'],
        black: ['before:to-black', 'after:to-black'],
        primary25: ['before:to-primary-25', 'after:to-primary-25'],
        yellow25: ['before:to-yellow-25', 'after:to-yellow-25'],
        gray50: ['before:to-gray-50', 'after:to-gray-50'],
        gray100: ['before:to-gray-100', 'after:to-gray-100'],
        gray200: ['before:to-gray-200', 'after:to-gray-200'],
        blue50: ['before:to-blue-50', 'after:to-blue-50'],
        primary800: ['before:to-primary-800', 'after:to-primary-800'],
        blue900: ['before:to-blue-900', 'after:to-blue-900'],
        gray900: ['before:to-gray-900', 'after:to-gray-900'],
      },
    },
  },
);

export const switcherTabArrowContainer = cva(
  [
    'absolute',
    'w-full',
    'h-[30px]',
    'rounded-[2px]',
    'border',
    'border-[#737475]',
    'z-20',
    'bg-gray-900',
    'flex',
    'items-center',
    'justify-center',
    'cursor-pointer',
  ],
  {
    variants: {
      isTop: {
        true: ['top-0'],
      },
      isBottom: {
        true: ['bottom-0'],
      },
      background: {
        white: ['bg-white'],
        black: ['bg-black'],
        primary25: ['bg-primary-25'],
        yellow25: ['bg-yellow-25'],
        gray50: ['bg-gray-50'],
        gray100: ['bg-gray-100'],
        gray200: ['bg-gray-200'],
        blue50: ['bg-blue-50'],
        primary800: ['bg-primary-800'],
        blue900: ['bg-blue-900'],
        gray900: ['bg-gray-900'],
      },
    },
  },
);

export const switcherTabItem = cva(
  [
    'flex',
    'flex-col',
    'gap-0',
    'lg:gap-[10px]',
    'px-[15px]',
    'py-[10px]',
    'w-full',
    'border',
    'rounded-[4px]',
    'cursor-pointer',
    'transition-all',
    'duration-500',
    'ease-in-out',
    'relative',
  ],
  {
    variants: {
      theme: {
        light: ['text-gray-900', 'border-transparent', 'hover:bg-primary-25'],
        dark: ['text-white', 'border-transparent', 'hover:bg-primary-700'],
      },
      isActive: {
        light: ['border-primary-600', 'bg-primary-25', 'py-[20px]'],
        dark: ['border-yellow-25', 'bg-primary-600', 'py-[20px]'],
      },
    },
  },
);

export const switcherContent = cva(
  [
    'w-full',
    'max-w-full',
    'lg:max-w-[70%]',
    'xl:max-w-[924px]',
    'p-[60px]',
    'flex',
    'items-center',
    'justify-center',
    'rounded-[4px]',
  ],
  {
    variants: {
      theme: {
        light: ['bg-gray-50', ''],
        dark: ['bg-primary-600', ''],
      },
      isMobile: {
        true: ['p-[10px]', 'rounded-[0_0_4px_4px]'],
      },
    },
  },
);

export const switcherItemContent = cva(
  [
    'flex',
    'flex-col',
    'transition-all',
    'duration-1000',
    'ease-in-out',
    'translate-y-[20px]',
    'opacity-0',
    'h-0',
    'invisible',
    'overflow-hidden',
  ],
  {
    variants: {
      active: {
        true: ['translate-y-0', 'opacity-100', 'h-fit', 'visible'],
      },
    },
  },
);

export const switchTabHeader = cva(['flex', 'items-center', 'gap-[10px]', 'relative'], {
  variants: {
    isMobile: {
      light: ['border', 'rounded-[4px]', ''],
      dark: ['border', 'rounded-[4px]', ''],
    },
  },
});

export const switchTabMobileHeader = cva(
  ['flex', 'items-center', 'gap-[10px]', 'relative', 'p-[10px]', 'rounded-[4px_4px_0_0]'],
  {
    variants: {
      theme: {
        light: ['border-0', 'border-b', 'border-primary-600', 'bg-primary-25'],
        dark: ['border', 'border-white', 'bg-primary-600'],
      },
    },
  },
);

export const switchMobileDropdownContainer = cva(['w-full', 'absolute', 'z-20', 'max-h-[350px]', 'p-[10px_5px]'], {
  variants: {
    theme: {
      light: ['border-b bg-white', 'border-primary-600', 'rounded-[0_0_4px_4px]'],
      dark: ['bg-primary-600'],
    },
    isOpen: {
      true: ['flex'],
      false: ['hidden'],
    },
  },
});

export const switchMobileDropdownItem = cva(
  ['flex', 'items-center', 'gap-[15px]', 'relative', 'p-[10px_5px]', 'rounded-[4px_4px_0_0]', 'h-[52px]'],
  {
    variants: {
      theme: {
        light: ['hover:bg-primary-25'],
        dark: ['hover:bg-primary-300'],
      },
    },
  },
);

export const switchTabIcon = cva(['size-[32px]', 'transition-all', 'duration-500', 'ease-in-out'], {
  variants: {
    theme: {
      light: ['brightness-0', 'grayscale'],
      dark: ['brightness-0', 'invert'],
    },
  },
});

export const dropdownTitle = cva(['font-semibold', 'text-sm'], {
  variants: {
    theme: {
      light: ['text-gray-700'],
      dark: ['text-gray-200'],
    },
  },
});
